import React from "react";
import { Check, BadgeCheck } from "lucide-react";
import {
    PRODUCT_NAME,
    TOOLKIT_NAME,
    COMPLETE_VALUE,
    LAUNCH_PRICE,
    SAVINGS,
    LAUNCH_COPY,
    CTA_SUBTEXT,
    TOOLKIT_INCLUDES,
    TOOLKIT_COUNT_HEADLINE,
    TOOLKIT_COUNT_BREAKDOWN,
    COMPLIANCE_DISCLAIMER,
    formatUSD,
} from "../../lib/wealthPricing";

/**
 * Authoritative pricing card used on Landing, Checkout (assessment step 6),
 * Confirmation/Results pages, and the Toolkit Library.
 *
 * Calm financial-services aesthetic — no countdown timers, no fake scarcity,
 * no urgency badges. Anchor price is the "Complete Toolkit Value" ($299) and
 * the launch price is $29.95. Savings line is mathematical, not promotional.
 *
 * variant:
 *   "hero"    — large stacked card with full includes list (Landing, Library)
 *   "compact" — slim horizontal summary (Checkout step, Post-booking upsell)
 *   "receipt" — confirmation receipt-style block (Results page)
 */
export default function PricingCard({ variant = "hero", className = "" }) {
    if (variant === "compact") {
        return (
            <div data-testid="pricing-card-compact" className={`rounded-xl border border-emerald-900/15 bg-white p-5 ${className}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-900 font-bold">Product</div>
                        <div className="font-serif text-lg text-emerald-950 leading-tight">{TOOLKIT_NAME}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-900 font-bold">Today's price</div>
                        <div className="font-serif text-2xl text-emerald-950 leading-none">
                            <span className="text-sm text-emerald-800 line-through mr-2 font-sans" data-testid="price-value-compact">{formatUSD(COMPLETE_VALUE)}</span>
                            <span data-testid="price-launch-compact">{formatUSD(LAUNCH_PRICE)}</span>
                        </div>
                    </div>
                </div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                    <BadgeCheck className="w-3.5 h-3.5" /> Launch Special · You save {formatUSD(SAVINGS)}
                </div>
                <p className="mt-3 text-[11px] text-emerald-900">{CTA_SUBTEXT}</p>
            </div>
        );
    }

    if (variant === "receipt") {
        return (
            <div data-testid="pricing-card-receipt" className={`rounded-xl border border-emerald-900/15 bg-emerald-50/40 px-6 py-5 ${className}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-900 font-bold">Receipt</div>
                        <div className="font-serif text-base sm:text-lg text-emerald-950 leading-tight mt-0.5">{TOOLKIT_NAME}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-900 font-bold">Paid</div>
                        <div className="font-serif text-xl text-emerald-950 leading-none mt-0.5">
                            <span className="text-xs text-emerald-800 line-through mr-1.5 font-sans">{formatUSD(COMPLETE_VALUE)}</span>
                            {formatUSD(LAUNCH_PRICE)}
                        </div>
                    </div>
                </div>
                <div className="mt-3 text-[11px] text-emerald-900">{CTA_SUBTEXT}</div>
            </div>
        );
    }

    // hero (default)
    return (
        <div data-testid="pricing-card-hero" className={`rounded-2xl border border-emerald-900/15 bg-white shadow-sm overflow-hidden ${className}`}>
            <div className="px-7 pt-7 pb-5 border-b border-emerald-900/10">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-bold">Product</div>
                        <h3 className="mt-1.5 font-serif text-xl sm:text-2xl text-emerald-950 leading-tight font-semibold">
                            {TOOLKIT_NAME}
                        </h3>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 flex-shrink-0">
                        <BadgeCheck className="w-3.5 h-3.5" /> Launch Special
                    </span>
                </div>
            </div>

            <div className="px-7 py-6">
                {/* Three-row price ladder: Value → Launch → Savings. Stacked on narrow
                    cards (post-booking column), inline rows on wide cards (landing). */}
                <div className="space-y-3">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-800 font-semibold">Complete Toolkit Value</div>
                        <div className="font-sans text-base text-emerald-800 line-through" data-testid="price-value">
                            {formatUSD(COMPLETE_VALUE)}
                        </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-800 font-bold">Today's Launch Price</div>
                        <div className="font-serif text-3xl sm:text-4xl text-emerald-950 leading-none font-semibold" data-testid="price-launch">
                            {formatUSD(LAUNCH_PRICE)}
                        </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-amber-800 font-bold">You Save</div>
                        <div className="font-serif text-lg text-amber-800 font-semibold" data-testid="price-savings">
                            {formatUSD(SAVINGS)}
                        </div>
                    </div>
                </div>

                <p className="mt-5 text-sm text-emerald-900/80 leading-relaxed" data-testid="launch-copy">
                    {LAUNCH_COPY}
                </p>

                <div className="mt-6 border-t border-emerald-900/10 pt-5">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-900 font-bold">Included with your Toolkit</div>
                        <div className="text-[11px] font-bold text-amber-800 tracking-wide" data-testid="toolkit-count-headline">{TOOLKIT_COUNT_HEADLINE}</div>
                    </div>
                    <div className="text-[11px] text-emerald-800 leading-snug mb-3" data-testid="toolkit-count-breakdown">{TOOLKIT_COUNT_BREAKDOWN}</div>
                    <ul className="space-y-3" data-testid="pricing-bullets">
                        {TOOLKIT_INCLUDES.map((item) => {
                            const title = typeof item === "string" ? item : item.title;
                            const meta = typeof item === "string" ? null : item.meta;
                            const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                            return (
                                <li key={title} className="flex items-start gap-2.5 text-sm text-emerald-950" data-testid={`includes-${slug}`}>
                                    <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-emerald-700 text-white flex items-center justify-center">
                                        <Check className="w-3 h-3" strokeWidth={3} />
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block font-semibold leading-tight">{title}</span>
                                        {meta && <span className="block text-[12px] text-emerald-900 mt-0.5 leading-snug">{meta}</span>}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <p className="mt-6 text-[11px] text-emerald-800 leading-relaxed border-t border-emerald-900/10 pt-4" data-testid="pricing-disclaimer">
                    <strong>{TOOLKIT_NAME}:</strong> {COMPLIANCE_DISCLAIMER}
                </p>
            </div>
        </div>
    );
}
